/**
 * Size labels.
 *
 * Spec v0.5 §1 has hours derived from size. That is inverted here, by decision:
 * hours are primary (they come from the estimator's decomposition) and the size
 * label is cosmetic, derived from the hours for display only. Nothing
 * downstream may read size — sprint load, capacity, projection and month end
 * all read the hours.
 *
 * ── Why the bands are stated in DAYS ────────────────────────────────────────
 * They used to be hours, as midpoints between four canonical figures (3/9/26/48
 * -> breaks at 6, 17.5, 37). Nobody could say what those numbers meant, because
 * they were not a judgement anybody had made — they were the arithmetic mean of
 * two other numbers. The judgement people actually make is in days: "that's a
 * couple of days", "that's most of a fortnight". So the bands are days, and the
 * one conversion factor a workspace needs (`hoursPerDay`) is stated once rather
 * than baked into four constants.
 *
 * ── Why they are configurable ───────────────────────────────────────────────
 * A day is not eight hours everywhere, and the line between Medium and Large is
 * a claim about a particular team's week. The defaults below are this product's
 * opinion; `workspaces.size_*` is where a workspace disagrees with it, and every
 * caller passes the workspace's thresholds rather than reaching for the default.
 * That is why the second parameter is REQUIRED: a call site that silently fell
 * back to the default would label a request one way on one screen and another
 * way on the next, with nothing to show which was wrong.
 */
/**
 * The product's opinion, and what a new workspace starts with.
 *
 * S up to 2 days · M up to 5 · L up to 10 · XL beyond that — an XL is more than
 * a fortnight of one person's time, which is the point at which the useful
 * answer is usually "this is several requests".
 */
export const DEFAULT_SIZE_THRESHOLDS = {
    hoursPerDay: 8,
    smallMaxDays: 2,
    mediumMaxDays: 5,
    largeMaxDays: 10,
};
/** The display label for an estimate. Zero hours means nobody has estimated it yet. */
export function sizeFromHours(hours, thresholds) {
    if (!Number.isFinite(hours) || hours <= 0)
        return 'needs_analysis';
    // A zero or negative day would divide every estimate into XL. The database
    // refuses to store one; this is what keeps a hand-built thresholds object
    // from doing it anyway.
    const hoursPerDay = thresholds.hoursPerDay > 0 ? thresholds.hoursPerDay : DEFAULT_SIZE_THRESHOLDS.hoursPerDay;
    const inDays = hours / hoursPerDay;
    if (inDays <= thresholds.smallMaxDays)
        return 'small';
    if (inDays <= thresholds.mediumMaxDays)
        return 'medium';
    if (inDays <= thresholds.largeMaxDays)
        return 'large';
    return 'xl';
}
export const SIZE_LABELS = {
    needs_analysis: 'Needs analysis',
    small: 'Small',
    medium: 'Medium',
    large: 'Large',
    xl: 'XL',
};
/** "2" rather than "2.00", and "1.5" rather than "1.50". */
function days(value) {
    return String(Math.round(value * 100) / 100);
}
/**
 * What a label means, in the workspace's own bands: "up to 2 days", "5–10 days".
 *
 * Shown beside the Size field, which is read-only and therefore invites the
 * question "why that one?". Derived from the same thresholds the label itself
 * is, so the explanation cannot drift from the rule.
 */
export function sizeRangeLabel(size, thresholds) {
    switch (size) {
        case 'needs_analysis':
            return 'no estimate yet';
        case 'small':
            return `up to ${days(thresholds.smallMaxDays)} days`;
        case 'medium':
            return `${days(thresholds.smallMaxDays)}–${days(thresholds.mediumMaxDays)} days`;
        case 'large':
            return `${days(thresholds.mediumMaxDays)}–${days(thresholds.largeMaxDays)} days`;
        case 'xl':
            return `${days(thresholds.largeMaxDays)}+ days`;
    }
}
//# sourceMappingURL=size.js.map